import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Icon,
  Label,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Spinner,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { DomainIcon } from '@patternfly/react-icons';
import Unauthenticated from '@app/Unauthenticated';
import { useApiFetch } from '../../../../api';
import { formatPrice } from '../../../../utils/money';
import { formatDateTime } from '../../../../utils/dateFormat';
import type { ApiResponseDomainOrderSingle } from '../../../../types/api/ApiResponseDomainOrder';
import type { DomainOrder, DomainOrderItem, DomainOrderType } from '../../../../types/models';
import { registrationFailureMessage } from './registrationFailure';

type ReturnPhase = 'confirming' | 'processing' | 'completed' | 'partially_failed' | 'failed' | 'error';

const MAX_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 2500;
// A registration waits for the registry to confirm the domain, which usually takes minutes but can
// take longer. After the fast window, keep checking a registration slowly so the page flips to
// "completed" by itself, then stop.
const SLOW_POLL_INTERVAL_MS = 30_000;
const SLOW_POLL_WINDOW_MS = 15 * 60_000;

/** Human label for the order kind — shown as a chip in the title bar. */
const ORDER_TYPE_LABEL: Record<DomainOrderType, string> = {
  registration: 'Registration',
  transfer: 'Transfer',
  trade: 'Owner change',
};

// Per-kind terminal/processing copy. The trade wording never says "registered" — an owner change
// creates nothing; only the registrant contact is replaced at the registry.
const COMPLETED_TITLE: Record<DomainOrderType, string> = {
  registration: 'All domains registered',
  transfer: 'All domains transferred in',
  trade: 'Owner change completed',
};
const COMPLETED_TAIL: Record<DomainOrderType, string> = {
  registration: ' and every domain in your order has been registered.',
  transfer: ' and every domain in your order has been transferred to us.',
  trade: ' and the new owner has been applied to every domain in your order.',
};
const PARTIAL_TITLE: Record<DomainOrderType, string> = {
  registration: "Some domains couldn't be registered",
  transfer: "Some domains couldn't be transferred",
  trade: "Some owner changes couldn't be completed",
};
const PARTIAL_LEAD: Record<DomainOrderType, string> = {
  registration: 'Part of your order was registered. ',
  transfer: 'Part of your order was transferred. ',
  trade: 'Part of your order was completed. ',
};
const FAILED_TITLE: Record<DomainOrderType, string> = {
  registration: 'Your order could not be completed',
  transfer: 'Your transfer could not be completed',
  trade: 'Your owner change could not be completed',
};
const FAILED_LEAD: Record<DomainOrderType, string> = {
  registration: 'None of the domains could be registered. ',
  transfer: 'None of the domains could be transferred. ',
  trade: 'None of the domains could have their owner changed. ',
};
const PROCESSING_TITLE: Record<DomainOrderType, string> = {
  registration: "We're finalizing your order",
  transfer: 'Your transfer has started',
  trade: "We're applying the owner change",
};

function itemStatusLabel(item: DomainOrderItem, orderType: DomainOrderType): React.ReactNode {
  switch (item.status) {
    case 'registered':
      return <Label color="green">Registered</Label>;
    case 'transferred':
      return <Label color="green">Transferred</Label>;
    case 'traded':
      return <Label color="green">Owner changed</Label>;
    case 'initiated':
      // Registration: the registrar accepted the purchase, the registry has not confirmed it yet.
      return <Label color="blue">{orderType === 'registration' ? 'Awaiting registry' : 'In progress'}</Label>;
    case 'failed':
      return <Label color="red">Failed</Label>;
    default:
      return <Label color="grey">Pending</Label>;
  }
}

// A transfer takes days and has no `registered_at`, so its items table drops the date column
// (Domain + Status only). Registration keeps "Registered at"; on a trade the same one-shot stamp
// means "the registrar accepted the registrant change", so it renders as "Changed at".
const ItemsTable: React.FunctionComponent<{
  items: DomainOrderItem[];
  orderType: DomainOrderType;
  waived: boolean;
}> = ({ items, orderType, waived }) => {
  const dateColumn = orderType === 'transfer' ? null : orderType === 'trade' ? 'Changed at' : 'Registered at';
  return (
    <Table aria-label="Order items" variant="compact" className="pf-v6-u-mt-md">
      <Thead>
        <Tr>
          <Th>Domain</Th>
          <Th>Status</Th>
          {dateColumn !== null && <Th>{dateColumn}</Th>}
        </Tr>
      </Thead>
      <Tbody>
        {items.map((item) => {
          const failureMessage =
            orderType === 'registration' && item.status === 'failed'
              ? registrationFailureMessage(item.error_code, waived)
              : null;
          return (
            <Tr key={item.id}>
              <Td dataLabel="Domain">{item.name}</Td>
              <Td dataLabel="Status">
                {itemStatusLabel(item, orderType)}
                {failureMessage !== null && (
                  <Content component="small" className="pf-v6-u-display-block pf-v6-u-mt-xs">
                    {failureMessage}
                  </Content>
                )}
              </Td>
              {dateColumn !== null && <Td dataLabel={dateColumn}>{formatDateTime(item.registered_at)}</Td>}
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );
};

const DomainOrderReturn: React.FunctionComponent = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const apiFetch = useApiFetch();

  const orderId = useMemo<number | null>(() => {
    if (id === undefined) {
      return null;
    }
    const parsed = Number(id);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [id]);

  const [phase, setPhase] = useState<ReturnPhase>('confirming');
  const [order, setOrder] = useState<DomainOrder | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [restartCount, setRestartCount] = useState(0);

  // Hold apiFetch in a ref so a background OIDC token renew doesn't re-run the polling effect and
  // reset a terminal phase back to 'confirming' (mirrors PaymentReturn).
  const apiFetchRef = useRef(apiFetch);
  useEffect(() => {
    apiFetchRef.current = apiFetch;
  }, [apiFetch]);

  useEffect(() => {
    if (orderId === null) {
      setPhase('error');
      return;
    }

    setPhase('confirming');
    setErrorMessage(null);

    let active = true;
    let attempts = 0;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const controller = new AbortController();
    const startedAt = Date.now();
    // Read from the fetched order, not the `order` state (stale inside this effect). Stays false
    // while no poll has succeeded, so an order of unknown kind is never slow-polled.
    let isRegistration = false;

    const poll = async () => {
      attempts += 1;
      let terminal = false;

      try {
        const response = await apiFetchRef.current(`/datacenters/domain-orders/${orderId}`, {
          signal: controller.signal,
        });
        const body = (await response.json().catch(() => null)) as ApiResponseDomainOrderSingle | null;

        if (body?.status === 'success' && body.data?.domain_order) {
          const fetched = body.data.domain_order;
          isRegistration = fetched.type === 'registration';
          if (active) {
            setOrder(fetched);
          }
          if (fetched.status === 'completed') {
            terminal = true;
            if (active) setPhase('completed');
            return;
          }
          if (fetched.status === 'partially_failed') {
            terminal = true;
            if (active) setPhase('partially_failed');
            return;
          }
          if (fetched.status === 'failed') {
            terminal = true;
            if (active) setPhase('failed');
            return;
          }
          if (active) {
            // Past the fast window the page has settled on 'processing'; a slow poll must not flip
            // it back to the 'confirming' spinner.
            setPhase(fetched.status === 'awaiting_payment' && attempts < MAX_ATTEMPTS ? 'confirming' : 'processing');
          }
        } else if (body?.status === 'error') {
          // A documented JSend error ("Order not found" for a missing/cross-tenant id) is
          // deterministic — surface it instead of optimistically retrying into "processing". The
          // order is created synchronously before the Mollie redirect, so a real return never hits
          // this; it only guards tampered/stale URLs.
          terminal = true;
          if (active) {
            setErrorMessage(body.message ?? null);
            setPhase('error');
          }
          return;
        }
      } catch {
        // Swallow AbortError / transient failures — treat as a non-terminal attempt and retry.
      }

      if (!active || terminal) {
        return;
      }

      if (attempts >= MAX_ATTEMPTS) {
        // Fulfilment runs on a background worker, so it's normal to still be processing after the
        // fast window. Land on a calm state, not an error.
        if (active) setPhase('processing');
        // Only a registration keeps checking, slowly, until the window (counted from page load)
        // closes; the other kinds keep the manual "Check again" (a transfer takes days).
        if (!isRegistration || Date.now() - startedAt + SLOW_POLL_INTERVAL_MS > SLOW_POLL_WINDOW_MS) {
          return;
        }
        timeoutId = setTimeout(() => {
          void poll();
        }, SLOW_POLL_INTERVAL_MS);
        return;
      }

      timeoutId = setTimeout(() => {
        void poll();
      }, POLL_INTERVAL_MS);
    };

    void poll();

    return () => {
      active = false;
      controller.abort();
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [orderId, restartCount]);

  const goToDomains = useCallback(() => {
    navigate('/datacenter/domains');
  }, [navigate]);

  const handleCheckAgain = useCallback(() => {
    setRestartCount((count) => count + 1);
  }, []);

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  const backButton = (
    <Button variant="primary" onClick={goToDomains}>
      Back to domains
    </Button>
  );

  // All order kinds land on this shared route; branch the copy (and the items table) on the kind.
  // A transfer takes days where a registration takes minutes and an owner change (trade) usually
  // completes quickly once the fulfilment worker picks it up.
  const kind: DomainOrderType = order?.type ?? 'registration';
  // A staff order skipped Mollie entirely (`payment_waived_at` set, `paid_at` stays null): no money
  // was collected, so never talk about "your payment" or promise a refund — its invoice is issued
  // unpaid instead.
  const waived = order != null && order.payment_waived_at != null;

  const renderState = () => {
    switch (phase) {
      case 'completed':
        return (
          <EmptyState
            status="success"
            titleText={COMPLETED_TITLE[kind]}
            headingLevel="h2"
            variant="lg"
            icon={DomainIcon}
          >
            <EmptyStateBody>
              {(waived ? 'The payment was waived for this staff order' : 'Your payment is confirmed') +
                COMPLETED_TAIL[kind]}
              {waived && ' An unpaid invoice has been issued to the client for later settlement.'}
              {order && <ItemsTable items={order.items} orderType={kind} waived={waived} />}
            </EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>{backButton}</EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        );
      // Refunds of failed items are issued by our team (there is no automatic Mollie refund), so
      // the copy promises a refund but never says "automatically".
      case 'partially_failed':
        return (
          <EmptyState status="warning" titleText={PARTIAL_TITLE[kind]} headingLevel="h2" variant="lg">
            <EmptyStateBody>
              {PARTIAL_LEAD[kind] +
                (waived
                  ? 'No payment was collected for this staff order, so there is nothing to refund — the domains that failed are simply not invoiced.'
                  : 'The domains that failed will be refunded — no action is needed on your part.')}
              {order && <ItemsTable items={order.items} orderType={kind} waived={waived} />}
            </EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>{backButton}</EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        );
      case 'failed':
        return (
          <EmptyState status="danger" titleText={FAILED_TITLE[kind]} headingLevel="h2" variant="lg">
            <EmptyStateBody>
              {FAILED_LEAD[kind] +
                (waived
                  ? 'No payment was collected for this staff order, so there is nothing to refund.'
                  : 'Your payment will be refunded — no action is needed on your part.')}
              {order && <ItemsTable items={order.items} orderType={kind} waived={waived} />}
            </EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>{backButton}</EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        );
      case 'processing': {
        const stillConfirming = order?.status === 'awaiting_payment';
        // When the order never loaded (every poll failed), stay neutral — we don't know the kind, so
        // we must not promise the registration's "usually within minutes" for what may be a multi-day
        // transfer.
        const processingTitle = stillConfirming
          ? order?.payment_source === 'card'
            ? "We're confirming the charge on your saved card"
            : "We're confirming your payment"
          : order === null
            ? "We're processing your order"
            : PROCESSING_TITLE[kind];
        // A waived (staff) order never sat in `awaiting_payment`, so "payment received" would be a
        // lie — it entered directly at `paid` with no money collected.
        const paymentLead = waived ? 'Payment waived (staff order).' : 'Payment received.';
        // A keep-contact transfer captured no registrant at order time — reassure the user that the
        // existing contact is preserved and will show up on its own.
        const keepContactNote = order?.keep_contact
          ? ' The registrant contact is kept from your current registrar and will be imported automatically once the transfer completes.'
          : '';
        // A registration item stays `pending` until the worker buys it, and turns `initiated` once the
        // registrar accepted the purchase; then only the registry's confirmation is outstanding. The
        // domain shows up in the domain list only once the registry has confirmed it.
        const placedWithRegistry =
          order !== null && order.items.length > 0 && order.items.every((item) => item.status !== 'pending');
        const processingBody = stillConfirming
          ? "We're still confirming your payment. This can take a moment — you can safely leave this page."
          : order === null
            ? 'Payment received. Your order is being processed — you can safely leave this page and check back later.'
            : kind === 'transfer'
              ? `${paymentLead} Your transfer has been started — this can take several days.${keepContactNote} You can safely leave this page; we'll email you when it's done.`
              : kind === 'trade'
                ? `${paymentLead} We are changing the owner of your domain(s) at the registry — this usually completes quickly, but can take a little while. You can safely leave this page and check back later.`
                : `${paymentLead} ${
                    placedWithRegistry
                      ? 'Your order has been placed with the registry.'
                      : "We're placing your order with the registry."
                  } Your domain(s) will appear in your domain list as soon as the registry confirms the registration, usually within minutes. You can safely leave this page.`;
        return (
          <EmptyState status="info" titleText={processingTitle} headingLevel="h2" variant="lg">
            <EmptyStateBody>
              {processingBody}
              {/* Per-item progress ("Awaiting registry") for a paid registration. */}
              {order !== null && kind === 'registration' && !stillConfirming && (
                <ItemsTable items={order.items} orderType={kind} waived={waived} />
              )}
            </EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>{backButton}</EmptyStateActions>
              <EmptyStateActions>
                <Button variant="link" onClick={handleCheckAgain}>
                  Check again
                </Button>
              </EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        );
      }
      case 'error':
        // "Order not found" is ALSO the documented outcome for a saved-card order whose charge
        // provably never happened: the throwaway order is discarded (≥15 min with no payment at
        // Mollie), so nothing was taken and the customer may simply order again. The same message
        // covers a tampered/cross-tenant id, so the copy stays neutral about which it was.
        return (
          <EmptyState
            status={errorMessage === 'Order not found' ? 'warning' : 'danger'}
            titleText="Order not found"
            headingLevel="h2"
            variant="lg"
          >
            <EmptyStateBody>
              {errorMessage === 'Order not found'
                ? 'This order no longer exists. If you had just confirmed an order paid with your saved card, the charge did not go through and nothing was taken — you can place the order again.'
                : (errorMessage ?? "We couldn't find that order. It may belong to another account.")}
            </EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>{backButton}</EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        );
      case 'confirming':
      default:
        // A saved-card order is charged in the background (`payment_source: 'card'`, `paid: false` at
        // creation): the order sits in `awaiting_payment` until the Mollie webhook lands, and this
        // poll sees it flip. A wallet-paid order is never in this phase (created directly at `paid`).
        if (order?.payment_source === 'card') {
          return (
            <EmptyState
              titleText="We're confirming the charge on your saved card"
              headingLevel="h2"
              variant="lg"
              icon={Spinner}
            >
              <EmptyStateBody>
                Your saved card is being charged in the background — no action is needed. This page updates
                automatically as soon as the payment is confirmed.
              </EmptyStateBody>
            </EmptyState>
          );
        }
        return (
          <EmptyState titleText="Confirming your payment…" headingLevel="h2" variant="lg" icon={Spinner}>
            <EmptyStateBody>This only takes a moment.</EmptyStateBody>
          </EmptyState>
        );
    }
  };

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="DomainOrderReturnBreadcrumb">
          <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
          <BreadcrumbItem to="/datacenter/domains">Domain Names</BreadcrumbItem>
          <BreadcrumbItem isActive>Order</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Content component="h1">
          <Icon size="lg" style={{ marginRight: '8px' }}>
            <DomainIcon />
          </Icon>
          Domain order
        </Content>
        <Content component="p">
          {order
            ? `Order #${order.id} — total ${formatPrice(order.amount_ttc)} (incl. VAT).`
            : 'Confirming the status of your domain order.'}
          {order && (
            <Label isCompact className="pf-v6-u-ml-sm">
              {ORDER_TYPE_LABEL[order.type]}
            </Label>
          )}
          {waived && (
            <Label color="purple" isCompact className="pf-v6-u-ml-sm">
              Payment waived (staff)
            </Label>
          )}
        </Content>
      </PageSection>
      <PageSection hasBodyWrapper={false}>{renderState()}</PageSection>
    </>
  );
};

export default DomainOrderReturn;
